import React, { useEffect, useState } from 'react';
import { Box, Typography, Alert, Dialog, DialogTitle, DialogContent, DialogActions, Button, Card, CardContent, useTheme, useMediaQuery, Chip, Stack, Tooltip } from '@mui/material';
import AccountBalanceOutlinedIcon from '@mui/icons-material/AccountBalanceOutlined';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SyncIcon from '@mui/icons-material/Sync';
import { useSnackbar } from 'notistack';
import { useNavigate, useSearchParams, useParams, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { notifyPermissionDenied } from '../../utils/apiPermissionError';
import facturaVentaService, { FacturaVenta, FacturaVentaDetalle } from '../../services/facturaVentaService';
import clienteService, { OPTIONS_CLIENTE_OPERATIVO } from '../../services/clienteService';
import productoService from '../../services/productoService';
import {
  evaluarUsoProductoDocumento,
  filtrarProductosInactivos,
  paramsBusquedaProductoDocumento,
} from '../../utils/bloqueaArtDiscontinuados';
import movMonedaService from '../../services/movMonedaService';
import pedidoVentaService from '../../services/pedidoVentaService';
import { transformarPedidoAFactura } from '../../utils/pedidoToFactura';
import { transformarCotizacionAFactura } from '../../utils/cotizacionToFactura';
import cotizacionVentaService from '../../services/cotizacionVentaService';
import ubicacionService from '../../services/ubicacionService';
import {
  VentaPendienteMaster,
  cantidadSugeridaDesdePendienteVenta,
} from '../../services/ventasSeguimientoService';
import { documentoOrigenSigueEnRenglones } from './pos/pendienteMetaLinea';
import FacturaForm from './components/FacturaForm';
import ConfirmCancelHotkeyDialog from '../../components/ConfirmCancelHotkeyDialog';
import ClienteSearch from '../../components/Tesoreria/ClienteSearch';
import ProductoSearch from '../pedidoventa/components/ProductoSearch';
import VerAsientoContableButton from '../../components/shared/VerAsientoContableButton';
import { usePostEmisionEnvioFacturaEmail } from '../../hooks/usePostEmisionEnvioFacturaEmail';
import { normalizarDetallesFacturaVentaParaEdicion } from '../../utils/normalizarDetallesFacturaVenta';
import { advertirLineasConPrecioCero } from '../../utils/pedidoVentaLineas';
import { primerErrorPrecioMinimoLineas } from '../../utils/validarPrecioMinimoLinea';
import { mensajeValidacionLotes } from '../../utils/loteLineaHelpers';
import { calcularFechaVencimDesdeVtoComprob, validarFechasFacturaVenta, sameCalendarDay } from '../../utils/facturaVentaFechas';
import { formatNroDocumentoVenta } from '../../utils/formatters';
import { resolveVtoComprobCliente } from '../../utils/vtoComprobCliente';
import { esComprobanteRegistrado, mapearVencimComprobanteCargado } from '../../utils/facturaVentaVencim';
import { FORMAS_DE_PAGO_VENTA } from '../../constants/formasDePagoVenta';
import { formaDePagoDesdeCliente } from '../../utils/formaPagoVenta';
import { obtenerCodigoListaCliente } from '../../utils/precioListaPedido';
import { resolverBonificacionesCliente } from '../../services/descuentoService';

type FacturaVentaUI = FacturaVenta & { ordenCompra?: string; transporte?: string; ubicacion?: string; formaDePago?: string; tipoIVA?: string; dni?: string; vtoComprob?: number | null };
type FacturaVentaUIExt = FacturaVentaUI & { nroSuc?: number; cuit?: string; dni?: string; tipoIVA?: string; idCompania?: string };

const obtenerFormaPagoSugerida = (cliente?: { condVta?: string | null } | null): string => {
  if (!cliente) return 'EFECTIVO';
  return formaDePagoDesdeCliente(cliente, FORMAS_DE_PAGO_VENTA, '');
};

const obtenerTransporteSugerido = (cliente?: { nroExpreso?: number | string | null } | null): string => {
  if (!cliente) {
    return '';
  }
  const rawValue = cliente.nroExpreso ?? (cliente as any)?.nroexpreso ?? null;
  if (rawValue === undefined || rawValue === null) {
    return '';
  }
  const strValue = String(rawValue).trim();
  if (!strValue || strValue === '0') {
    return '';
  }
  return strValue;
};

function isSuperAdmin(roles: string[] | undefined): boolean {
  return (roles ?? []).some((r) => String(r).toUpperCase() === 'SUPERADMIN');
}

const NuevaFacturaVenta: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const superAdmin = isSuperAdmin(user?.roles);
  const [generandoAsiento, setGenerandoAsiento] = useState(false);
  const [openGenerarAsientoDialog, setOpenGenerarAsientoDialog] = useState(false);
  const [searchParams] = useSearchParams();
  const { nroFormulario: nroFormularioParam } = useParams<{ nroFormulario?: string }>();
  const location = useLocation();
  const pedidoIdParam = searchParams.get('pedidoId');
  const cotizacionIdParam = searchParams.get('cotizacionId');
  const facturaIdParam = searchParams.get('facturaId');
  const modoVer = Boolean(nroFormularioParam && location.pathname.includes('/ver'));
  const modoEditar = Boolean(nroFormularioParam && location.pathname.includes('/editar'));
  const theme = useTheme();
  const _isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const [loading, setLoading] = useState(false);
  const [loadingPedido, setLoadingPedido] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pedidoId, setPedidoId] = useState<number | null>(pedidoIdParam ? parseInt(pedidoIdParam, 10) : null);
  const [cotizacionId, setCotizacionId] = useState<number | null>(cotizacionIdParam ? parseInt(cotizacionIdParam, 10) : null);
  const [facturaOrigenId, setFacturaOrigenId] = useState<number | null>(facturaIdParam ? parseInt(facturaIdParam, 10) : null);
  const [formaPagoDelPedido, setFormaPagoDelPedido] = useState<string | null>(null);
  const [transporteDelPedido, setTransporteDelPedido] = useState<string | null>(null); // Estado para transporte del pedido
  const [nombreDelPedido, setNombreDelPedido] = useState<string | null>(null); // Estado para nombre del pedido
  const [cuitDelDocumento, setCuitDelDocumento] = useState<string | null>(null); // Estado para cuit/dni del pedido/cotización
  const [tipoIVADelDocumento, setTipoIVADelDocumento] = useState<string | null>(null); // Estado para tipoIVA del pedido/cotización
  const [infoPedido, setInfoPedido] = useState<{ letra?: string; puntoVenta?: number; nroComprobante?: number; nroFormulario?: number } | null>(null);
  const [facturaVenta, setFacturaVenta] = useState<FacturaVentaUIExt>({
    nroFormulario: 0,
    nroTipoFormulario: '12',
    tipoCliente: 'C',
    fechaEmision: new Date(),
    fechaVencim: new Date(),
    horaEmision: new Date(),
    nroCliente: 0,
    nombre: '',
    nroVendedor: 0,
    nroLista: '',
    codigoMoneda: 0,
    importeTotal: 0,
    asentado: 'N',
    descripcion: '',
    usuario: '',
    ordenCompra: '',
    transporte: '',
    ubicacion: '',
    formaDePago: '',
    nroSuc: 0,
    cuit: '',
    dni: '',
    tipoIVA: '',
    idCompania: '',
    detalles: [],
    porcDesc: 0,
    porcDesc2: 0,
    porcDesc3: 0,
  });
  const [clienteDialogOpen, setClienteDialogOpen] = useState(false);
  const [productoDialogOpen, setProductoDialogOpen] = useState(false);
  const [productos, setProductos] = useState<any[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [successDialogOpen, setSuccessDialogOpen] = useState(false);
  const [confirmSalirOpen, setConfirmSalirOpen] = useState(false);
  const [nroFacturaCreada, setNroFacturaCreada] = useState<number | null>(null);
  const [comprobanteFacturaCreada, setComprobanteFacturaCreada] = useState<string | null>(null);
  const { enqueueSnackbar } = useSnackbar();
  const {
    ofrecerTrasEmision,
    resetOfferedTracking,
    emailUi: postEmisionEmailUi,
  } = usePostEmisionEnvioFacturaEmail();
  const [cotizacionUSD, setCotizacionUSD] = useState<number | null>(null);
  const [pendingApplied, setPendingApplied] = useState(false);
  const round2 = (n: number) => Math.round(n * 100) / 100;
  const ventaPendiente = (location.state as { ventaPendiente?: VentaPendienteMaster } | null)?.ventaPendiente;

  useEffect(() => {
    if (!successDialogOpen || !nroFacturaCreada) return;
    const timer = window.setTimeout(() => {
      void ofrecerTrasEmision(nroFacturaCreada);
    }, 200);
    return () => window.clearTimeout(timer);
  }, [successDialogOpen, nroFacturaCreada, ofrecerTrasEmision]);

  useEffect(() => {
    (async () => {
      try {
        const v = await movMonedaService.getUltimaCotizacion();
        if (v && Number.isFinite(v) && v > 0) {
          setCotizacionUSD(v);
        } else {
          setCotizacionUSD(null);
        }
      } catch {
        setCotizacionUSD(null);
      }
    })();
  }, []);

  useEffect(() => {
    if (
      pendingApplied ||
      !ventaPendiente ||
      (pedidoId && !isNaN(pedidoId)) ||
      (cotizacionId && !isNaN(cotizacionId)) ||
      modoEditar ||
      modoVer
    ) {
      return;
    }

    setFacturaVenta((prev) => {
      const alreadyIncluded = (prev.detalles || []).some(
        (d) =>
          d.nroFormularioMaster === ventaPendiente.nroFormularioMaster &&
          d.nroItemMaster === ventaPendiente.nroItemMaster
      );
      if (alreadyIncluded) return prev;

      const cantidadPendienteFacturar = Number(ventaPendiente.pendienteFacturar) || 0;
      const cantidad =
        cantidadPendienteFacturar > 0
          ? cantidadPendienteFacturar
          : cantidadSugeridaDesdePendienteVenta(ventaPendiente);
      const precioLista = Number(ventaPendiente.precioListaMaster ?? ventaPendiente.precioUnitarioMaster ?? 0) || 0;
      const porcDesc = Number(ventaPendiente.porcDesc1Master ?? 0) || 0;
      const precioUnitario = precioLista - (precioLista * porcDesc) / 100;
      const subtotal = round2(cantidad * precioUnitario);

      const nuevoDetalle: FacturaVentaDetalle = {
        nroIdformulario: prev.nroFormulario,
        nroItem: (prev.detalles?.length || 0) + 1,
        nroFormularioMaster: ventaPendiente.nroFormularioMaster,
        nroItemMaster: ventaPendiente.nroItemMaster,
        codigoArticulo: ventaPendiente.codigoArticulo || '',
        descripcionArticulo: ventaPendiente.descripcionArticulo || '',
        cantidad,
        precioUnitario: precioLista,
        porcDesc,
        subtotal,
      };

      return {
        ...prev,
        nroCliente:
          ventaPendiente.nroCliente && ventaPendiente.nroCliente > 0
            ? ventaPendiente.nroCliente
            : prev.nroCliente,
        nombre: ventaPendiente.nombreCliente || prev.nombre,
        detalles: [...(prev.detalles || []), nuevoDetalle],
        importeTotal: round2((prev.importeTotal || 0) + subtotal),
      };
    });

    setPendingApplied(true);
  }, [pendingApplied, ventaPendiente, pedidoId, cotizacionId, modoEditar, modoVer]);

  // Cargar factura existente en modo visualizar o editar — datos del comprobante
  useEffect(() => {
    if ((!modoVer && !modoEditar) || !nroFormularioParam) return;
    const id = parseInt(nroFormularioParam, 10);
    if (isNaN(id)) return;
    let cancelled = false;
    (async () => {
      try {
        setLoadingPedido(true);
        setError(null);
        const data = await facturaVentaService.getFacturaVenta(id);
        if (cancelled) return;
        const f = data as any;
        // Resolver ubicación desde nroUbicacionOrigen del comprobante (no del cliente)
        let ubicacionStr = f.ubicacion ?? '';
        if (!ubicacionStr && f.nroUbicacionOrigen) {
          try {
            const u = await ubicacionService.getUbicacion(f.nroUbicacionOrigen);
            if (u) ubicacionStr = `${u.nombreUbicacion ?? ''} (${u.nroUbicacion})`.trim();
            else ubicacionStr = `(${f.nroUbicacionOrigen})`;
          } catch {
            ubicacionStr = `(${f.nroUbicacionOrigen})`;
          }
        }
        const fechaEmisionCargada = f.fechaEmision ? new Date(f.fechaEmision) : new Date();
        const vencimComprobante = mapearVencimComprobanteCargado(f, fechaEmisionCargada);
        setFacturaVenta({
          ...f,
          fechaEmision: fechaEmisionCargada,
          fechaVencim: vencimComprobante.fechaVencim,
          vtoComprob: vencimComprobante.vtoComprob,
          horaEmision: f.horaEmision ? new Date(f.horaEmision) : new Date(),
          ordenCompra: f.ordenDeCompra ?? f.ordenCompra ?? '',
          transporte: f.formaEntrega ?? f.transporte ?? '',
          ubicacion: ubicacionStr,
          formaDePago: f.formaDePago ?? '',
          nroSuc: f.nroSuc ?? 0,
          cuit: f.cuit ?? '',
          dni: f.dni ?? '',
          tipoIVA: f.tipoIva ?? f.tipoIVA ?? '',
          idCompania: f.idCompania ?? '',
          formaEntrega: f.formaEntrega ?? '',
          nroUbicacionOrigen: f.nroUbicacionOrigen ?? 0,
          detalles: normalizarDetallesFacturaVentaParaEdicion(f.detalles),
        } as FacturaVentaUIExt);
      } catch (err: any) {
        if (!cancelled) {
          setError(err?.response?.data?.message || err?.message || 'Error al cargar la factura');
          enqueueSnackbar('Error al cargar la factura', { variant: 'error' });
        }
      } finally {
        if (!cancelled) setLoadingPedido(false);
      }
    })();
    return () => { cancelled = true; };
  }, [modoVer, modoEditar, nroFormularioParam, enqueueSnackbar]);

  // Cargar datos del pedido o cotización si existe pedidoId o cotizacionId
  useEffect(() => {
    const cargarDesdePedido = async () => {
      if (!pedidoId || isNaN(pedidoId)) return;
      try {
        setLoadingPedido(true);
        setError(null);
        const pedido = await pedidoVentaService.getPedidoVenta(pedidoId);
        const pedidoExt = pedido as any;
          
          // Guardar información del pedido para mostrar en el chip
          // El punto de venta es nroSuc, no puntoVenta
          setInfoPedido({
            letra: pedidoExt.letra || '',
            puntoVenta: pedidoExt.nroSuc || pedidoExt.puntoVenta || pedidoExt.puntoDeVenta || pedidoExt.nroPuntoVenta || 0,
            nroComprobante: pedidoExt.nroComprobante || pedidoExt.numeroComprobante || 0,
            nroFormulario: pedido.nroFormulario
          });
          
          // Transformar el pedido a factura
          const facturaTransformada = await transformarPedidoAFactura(pedido);
          
          // Pre-cargar todos los campos en el estado
          // La función transformarPedidoAFactura ya busca variaciones de nombres, pero verificamos también directamente del pedido
          
          // Guardar la forma de pago del pedido para que no se sobrescriba cuando se carga el cliente
          const formaPagoPedido = pedidoExt.formaDePago || facturaTransformada.formaDePago || '';
          if (formaPagoPedido) {
            setFormaPagoDelPedido(formaPagoPedido);
          }
          
          // Guardar el transporte del pedido para que no se sobrescriba cuando se carga el cliente
          const transportePedido = facturaTransformada.transporte || '';
          if (transportePedido) {
            setTransporteDelPedido(transportePedido);
          }
          
          // Guardar el nombre del pedido para que no se sobrescriba cuando se carga el cliente
          const nombrePedido = facturaTransformada.nombre || pedido.nombre || '';
          if (nombrePedido) {
            setNombreDelPedido(nombrePedido);
          }
          const cuitDoc = (facturaTransformada as any).cuit || pedidoExt.cuit || '';
          const tipoIVADoc = (facturaTransformada as any).tipoIVA || pedidoExt.tipoIva || pedidoExt.tipoIVA || '';
          if (cuitDoc) setCuitDelDocumento(cuitDoc);
          if (tipoIVADoc) setTipoIVADelDocumento(tipoIVADoc);
          const fechaEmisionPedido = facturaTransformada.fechaEmision
            ? (facturaTransformada.fechaEmision instanceof Date
              ? facturaTransformada.fechaEmision
              : new Date(facturaTransformada.fechaEmision))
            : new Date();
          setFacturaVenta({
            ...facturaTransformada,
            fechaEmision: fechaEmisionPedido,
            fechaVencim: calcularFechaVencimDesdeVtoComprob(fechaEmisionPedido, null),
            ordenCompra: pedidoExt.ordenCompra || pedidoExt.ordenDeCompra || facturaTransformada.ordenCompra || '',
            transporte: transportePedido, // Usar el transporte de la transformación (ya busca nroExpreso)
            ubicacion: facturaTransformada.ubicacion || '',
            formaDePago: formaPagoPedido,
            nroSuc: facturaTransformada.nroSuc || 0,
            cuit: facturaTransformada.cuit || '',
            dni: (facturaTransformada as any).dni || '',
            tipoIVA: (facturaTransformada as any).tipoIVA || '',
            idCompania: facturaTransformada.idCompania || '',
          } as FacturaVentaUIExt);
        } catch (err: any) {
          console.error('Error al cargar el pedido: ', err);
          const message = err?.response?.data || 'Error al cargar el pedido para facturar';
          setError(message);
          enqueueSnackbar(message, { variant: 'error', autoHideDuration: 5000 });
          setPedidoId(null);
        } finally {
          setLoadingPedido(false);
        }
    };

    const cargarDesdeCotizacion = async () => {
      if (!cotizacionId || isNaN(cotizacionId)) return;
      try {
        setLoadingPedido(true);
        setError(null);
        const cotizacion = await cotizacionVentaService.getCotizacionVenta(cotizacionId);
        const cotizacionExt = cotizacion as any;
        setInfoPedido({
          letra: cotizacionExt.letra || '',
          puntoVenta: cotizacionExt.nroSuc || 0,
          nroComprobante: cotizacionExt.nroComprobante || 0,
          nroFormulario: cotizacion.nroFormulario
        });
        const facturaTransformada = await transformarCotizacionAFactura(cotizacion);
        const formaPagoPedido = cotizacionExt.formaDePago || facturaTransformada.formaDePago || '';
        if (formaPagoPedido) setFormaPagoDelPedido(formaPagoPedido);
        const transportePedido = facturaTransformada.transporte || '';
        if (transportePedido) setTransporteDelPedido(transportePedido);
        const nombrePedido = facturaTransformada.nombre || cotizacion.nombre || '';
        if (nombrePedido) setNombreDelPedido(nombrePedido);
        const cuitDoc = (facturaTransformada as any).cuit || cotizacionExt.cuit || '';
        const tipoIVADoc = (facturaTransformada as any).tipoIVA || cotizacionExt.tipoIva || cotizacionExt.tipoIVA || '';
        if (cuitDoc) setCuitDelDocumento(cuitDoc);
        if (tipoIVADoc) setTipoIVADelDocumento(tipoIVADoc);
        const fechaEmisionCot = facturaTransformada.fechaEmision
          ? (facturaTransformada.fechaEmision instanceof Date
            ? facturaTransformada.fechaEmision
            : new Date(facturaTransformada.fechaEmision))
          : new Date();
        const fechaVencimCot = facturaTransformada.fechaVencim
          ? (facturaTransformada.fechaVencim instanceof Date
            ? facturaTransformada.fechaVencim
            : new Date(facturaTransformada.fechaVencim))
          : calcularFechaVencimDesdeVtoComprob(fechaEmisionCot, facturaTransformada.vtoComprob ?? null);
        setFacturaVenta({
          ...facturaTransformada,
          fechaEmision: fechaEmisionCot,
          fechaVencim: fechaVencimCot,
          vtoComprob: facturaTransformada.vtoComprob ?? null,
          ordenCompra: cotizacionExt.ordenDeCompra || cotizacionExt.ordenCompra || facturaTransformada.ordenCompra || '',
          transporte: transportePedido,
          ubicacion: facturaTransformada.ubicacion || '',
          formaDePago: formaPagoPedido,
          nroSuc: facturaTransformada.nroSuc || 0,
          cuit: facturaTransformada.cuit || '',
          dni: (facturaTransformada as any).dni || '',
          tipoIVA: (facturaTransformada as any).tipoIVA || '',
          idCompania: facturaTransformada.idCompania || '',
        } as FacturaVentaUIExt);
      } catch (err: any) {
        console.error('Error al cargar la cotización: ', err);
        const message = err?.response?.data || 'Error al cargar la cotización para facturar';
        setError(message);
        enqueueSnackbar(message, { variant: 'error', autoHideDuration: 5000 });
        setCotizacionId(null);
      } finally {
        setLoadingPedido(false);
      }
    };

    const cargarDesdeFacturaNoCategorizada = async () => {
      if (!facturaOrigenId || isNaN(facturaOrigenId)) return;
      try {
        setLoadingPedido(true);
        setError(null);
        const data = await facturaVentaService.getFacturaVenta(facturaOrigenId);
        const f = data as any;

        let ubicacionStr = f.ubicacion ?? '';
        if (!ubicacionStr && f.nroUbicacionOrigen) {
          try {
            const u = await ubicacionService.getUbicacion(f.nroUbicacionOrigen);
            if (u) ubicacionStr = `${u.nombreUbicacion ?? ''} (${u.nroUbicacion})`.trim();
            else ubicacionStr = `(${f.nroUbicacionOrigen})`;
          } catch {
            ubicacionStr = `(${f.nroUbicacionOrigen})`;
          }
        }

        const detallesTransformados: FacturaVentaDetalle[] = (data.detalles || []).map((detalle: any, index: number) => ({
          nroIdformulario: 0,
          nroItem: index + 1,
          codigoArticulo: detalle.codigoArticulo ?? '',
          descripcionArticulo: detalle.descripcionArticulo ?? '',
          cantidad: detalle.cantidad ?? 0,
          precioLista: detalle.precioLista ?? detalle.precioUnitario ?? 0,
          porcDesc: detalle.porcDesc ?? 0,
          precioUnitario: detalle.precioUnitario ?? detalle.precioLista ?? 0,
          descuento: detalle.descuento ?? 0,
          subtotal: detalle.subtotal ?? undefined,
          notas: detalle.notas ?? '',
        }));

        const importeTotal = detallesTransformados.reduce((sum, det) => sum + (det.subtotal || 0), 0);

        setFacturaVenta((prev: FacturaVentaUIExt) => ({
          ...prev,
          nroFormulario: 0,
          fechaEmision: new Date(),
          horaEmision: new Date(),
          nroCliente: f.nroCliente ?? 0,
          nombre: f.nombre ?? '',
          nroVendedor: f.nroVendedor ?? 0,
          nroLista: f.nroLista ?? '',
          codigoMoneda: f.codigoMoneda ?? 0,
          descripcion: f.descripcion ?? '',
          ordenCompra: f.ordenDeCompra ?? f.ordenCompra ?? '',
          transporte: f.formaEntrega ?? f.transporte ?? '',
          ubicacion: ubicacionStr,
          formaDePago: f.formaDePago ?? '',
          nroSuc: f.nroSuc ?? 0,
          cuit: f.cuit ?? '',
          dni: f.dni ?? '',
          tipoIVA: f.tipoIva ?? f.tipoIVA ?? '',
          idCompania: f.idCompania ?? '',
          detalles: detallesTransformados,
          importeTotal: importeTotal,
        }));
      } catch (err: any) {
        console.error('Error al cargar la factura no categorizada: ', err);
        const message = err?.response?.data || 'Error al cargar la factura para facturar';
        setError(message);
        enqueueSnackbar(message, { variant: 'error', autoHideDuration: 5000 });
        setFacturaOrigenId(null);
      } finally {
        setLoadingPedido(false);
      }
    };

    if (modoVer || modoEditar) return;
    if (pedidoId && !isNaN(pedidoId)) {
      cargarDesdePedido();
    } else if (cotizacionId && !isNaN(cotizacionId)) {
      cargarDesdeCotizacion();
    } else if (facturaOrigenId && !isNaN(facturaOrigenId)) {
      cargarDesdeFacturaNoCategorizada();
    }
  }, [modoVer, pedidoId, cotizacionId, facturaOrigenId, enqueueSnackbar]);

  const handleProductoSearch = async () => {
    try {
      setLoading(true);
      const paramsBloqueo = await paramsBusquedaProductoDocumento('VENTAS');
      const response = await productoService.getProductos({
        searchTerm,
        pageNumber: 1,
        pageSize: 50,
        ...paramsBloqueo,
      });
      setProductos(filtrarProductosInactivos(response.items || []));
    } catch (err: any) {
      enqueueSnackbar('Error al buscar productos', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleClienteSelect = (cliente: any) => {
    const transporteSugerido = obtenerTransporteSugerido(cliente);
    const formaPagoSugerida = obtenerFormaPagoSugerida(cliente);
    const vtoComprobCliente = resolveVtoComprobCliente(cliente);
    setFacturaVenta((prev: FacturaVentaUIExt) => {
      const comprobanteRegistrado = modoEditar || modoVer || esComprobanteRegistrado(prev.nroFormulario);
      const fechaVencim = comprobanteRegistrado
        ? (prev.fechaVencim ?? calcularFechaVencimDesdeVtoComprob(prev.fechaEmision ?? new Date(), prev.vtoComprob))
        : calcularFechaVencimDesdeVtoComprob(prev.fechaEmision ?? new Date(), vtoComprobCliente);
      return {
      ...prev,
      nroCliente: cliente.nroCliente ?? cliente.codigo ?? 0,
      nombre: cliente.nombre,
      cuit: cliente.cuit || '',
      dni: (cliente as any).dni || '',
      tipoIVA: cliente.tipoIva || '',
      vtoComprob: comprobanteRegistrado ? prev.vtoComprob : vtoComprobCliente,
      fechaVencim,
      nroVendedor:
        modoEditar || modoVer
          ? prev.nroVendedor
          : Number((cliente as any).vendedor) > 0
            ? Number((cliente as any).vendedor)
            : 0,
      // Respetar la forma de pago del pedido si existe, sino usar la del cliente
      formaDePago: formaPagoDelPedido || formaPagoSugerida,
      transporte: transporteSugerido,
    };
    });
    setClienteDialogOpen(false);
    const nroSel = cliente.nroCliente ?? cliente.codigo ?? 0;
    void resolverBonificacionesCliente(cliente, {
      codigoLista: obtenerCodigoListaCliente(cliente),
      nroVendedor: Number(cliente?.nroVendedor ?? cliente?.vendedor) || null,
    }).then((desc) => {
      setFacturaVenta((prev) => {
        if (Number(prev.nroCliente) !== Number(nroSel)) return prev;
        return { ...prev, porcDesc: desc.porcDesc, porcDesc2: desc.porcDesc2, porcDesc3: desc.porcDesc3 };
      });
    });
  };

  const handleProductoSelect = async (producto: any) => {
    if (!facturaVenta.nroCliente || facturaVenta.nroCliente <= 0) {
      enqueueSnackbar('Debe seleccionar un cliente antes de agregar productos al carrito', { variant: 'warning' });
      return;
    }
    const decision = await evaluarUsoProductoDocumento(
      {
        codigoArticulo: producto.codigoArticulo,
        tipoArticulo: producto.tipoArticulo,
        descripcion: producto.descripcion,
      },
      'VENTAS',
    );
    if (!decision.permitido) {
      if (decision.motivo !== 'discontinuado_rechazado') {
        enqueueSnackbar(decision.mensaje, { variant: 'warning' });
      }
      return;
    }
    const base = Number(producto.precioVenta ?? producto.precio ?? producto.costo ?? producto.precioLista ?? producto.precioUnitario ?? 0);
    const tipoMoneda = producto.tipoMoneda ?? producto.tipoMonedaArticulo ?? 0;
    const baseRed = round2(base);
    const precio = (Number(tipoMoneda) === 1 && cotizacionUSD && cotizacionUSD > 0) ? round2(baseRed * cotizacionUSD) : baseRed;
    const nuevoDetalle: FacturaVentaDetalle = {
      nroIdformulario: facturaVenta.nroFormulario,
      nroItem: (facturaVenta.detalles?.length || 0) + 1,
      codigoArticulo: producto.codigoArticulo,
      descripcionArticulo: producto.descripcion,
      cantidad: 1,
      precioUnitario: precio,
      descuento: 0,
      subtotal: precio,
    };

    setFacturaVenta((prev: FacturaVentaUIExt) => ({
      ...prev,
      detalles: [...(prev.detalles || []), nuevoDetalle],
      importeTotal: (prev.importeTotal || 0) + precio,
    }));
    setProductoDialogOpen(false);
  };

  useEffect(() => {
    const v = Number(facturaVenta.nroSuc);
    if (Number.isFinite(v) && v > 0) {
      try {
        localStorage.setItem('nroSuc', String(v));
        localStorage.setItem('sucursal', String(v));
      } catch {}
    }
  }, [facturaVenta.nroSuc]);

  useEffect(() => {
    if (modoVer || modoEditar) return; // En modo visualizar o editar no sobrescribir con datos del cliente; se muestran los del comprobante
    const value = facturaVenta.nroCliente;
    const handle = setTimeout(() => {
      const nro = parseInt((value ?? 0).toString());
      if (!isNaN(nro) && nro > 0) {
        (async () => {
          try {
            const c = await clienteService.getCliente(nro, OPTIONS_CLIENTE_OPERATIVO);
            const transporteSugerido = obtenerTransporteSugerido(c);
            const formaPagoSugerida = obtenerFormaPagoSugerida(c);
            const desc = await resolverBonificacionesCliente(c, {
              codigoLista: obtenerCodigoListaCliente(c),
              nroVendedor: Number((c as any).nroVendedor ?? (c as any).vendedor) || null,
            });
            setFacturaVenta(prev => {
              const transporteFinal = prev.transporte || transporteDelPedido || transporteSugerido;
              // Respetar el nombre del pedido si existe, sino usar el del cliente
              const nombreFinal = nombreDelPedido || prev.nombre || c.nombre;
              // Respetar cuit y tipoIVA del pedido/cotización si existen, sino usar los del cliente
              const cuitFinal = cuitDelDocumento ?? prev.cuit ?? (c.cuit || '');
              const tipoIVAFinal = tipoIVADelDocumento ?? prev.tipoIVA ?? (c.tipoIva || '');
              // Para consumidor final, preservar dni del documento si existe
              const dniFinal = ((tipoIVAFinal === 'C' || tipoIVAFinal === 'CF') && prev.dni) ? prev.dni : ((c as any).dni || '');
              const nroVendedorFinal =
                prev.nroVendedor != null && prev.nroVendedor > 0
                  ? prev.nroVendedor
                  : Number(c.vendedor) > 0
                    ? Number(c.vendedor)
                    : 0;
              const vtoComprobCliente = resolveVtoComprobCliente(c);
              const comprobanteRegistrado = esComprobanteRegistrado(prev.nroFormulario);
              let fechaVencim = prev.fechaVencim ?? prev.fechaEmision;
              let vtoComprob = prev.vtoComprob ?? vtoComprobCliente;
              if (!comprobanteRegistrado) {
                const newCalc = calcularFechaVencimDesdeVtoComprob(
                  prev.fechaEmision ?? new Date(),
                  vtoComprobCliente,
                );
                const oldCalc = calcularFechaVencimDesdeVtoComprob(
                  prev.fechaEmision ?? new Date(),
                  prev.vtoComprob ?? null,
                );
                const mantenerVencimManual = !sameCalendarDay(prev.fechaVencim ?? prev.fechaEmision, oldCalc);
                fechaVencim = mantenerVencimManual ? (prev.fechaVencim ?? newCalc) : newCalc;
                vtoComprob = vtoComprobCliente;
              }
              return {
                ...prev,
                nroCliente: c.nroCliente,
                nombre: nombreFinal,
                cuit: cuitFinal,
                dni: dniFinal,
                tipoIVA: tipoIVAFinal,
                nroVendedor: nroVendedorFinal,
                vtoComprob,
                fechaVencim,
                // Respetar la forma de pago del pedido si existe, sino usar la del cliente
                formaDePago: formaPagoDelPedido || formaPagoSugerida,
                // Respetar el transporte del pedido: primero el que ya está en facturaVenta (del pedido),
                // luego el guardado en transporteDelPedido, y finalmente el del cliente
                transporte: transporteFinal,
                porcDesc: desc.porcDesc,
                porcDesc2: desc.porcDesc2,
                porcDesc3: desc.porcDesc3,
              };
            });
          } catch {}
        })();
      }
    }, 500);
    return () => clearTimeout(handle);
  }, [modoVer, modoEditar, facturaVenta.nroCliente, nombreDelPedido, transporteDelPedido, formaPagoDelPedido, cuitDelDocumento, tipoIVADelDocumento]);

  const handleSave = async () => {
    try {
      setLoading(true);
      setError(null);

      const erroresValidacion: string[] = [];

      if (!facturaVenta.nroCliente || facturaVenta.nroCliente <= 0) {
        erroresValidacion.push('Debe seleccionar un cliente');
      }

      if (!facturaVenta.fechaEmision) {
        erroresValidacion.push('Debe ingresar la Fecha de Emisión en la sección Información de la Factura');
      }

      const fechaVencimError = validarFechasFacturaVenta(
        facturaVenta.fechaEmision,
        facturaVenta.fechaVencim ?? facturaVenta.fechaEmision,
        calcularFechaVencimDesdeVtoComprob(
          facturaVenta.fechaEmision,
          facturaVenta.vtoComprob,
        ),
      );
      if (fechaVencimError) {
        erroresValidacion.push(fechaVencimError);
      }

      const ubicacion = (facturaVenta as FacturaVentaUI).ubicacion;
      if (!ubicacion || ubicacion.trim() === '') {
        erroresValidacion.push('Debe seleccionar una Ubicación en la sección Información de la Factura');
      }

      const formaDePago = (facturaVenta as FacturaVentaUI).formaDePago;
      if (!formaDePago || formaDePago.trim() === '') {
        erroresValidacion.push('Debe seleccionar una Forma de Pago en la sección Información de la Factura');
      }

      if (!facturaVenta.detalles || facturaVenta.detalles.length === 0) {
        erroresValidacion.push('Debe agregar al menos un producto a la factura');
      } else {
        facturaVenta.detalles.forEach((detalle, index) => {
          if (!detalle.cantidad || detalle.cantidad <= 0) {
            erroresValidacion.push(`El producto "${detalle.descripcionArticulo || detalle.codigoArticulo}" (item ${index + 1}) debe tener una cantidad mayor a 0`);
          }
          if (!detalle.codigoArticulo) {
            erroresValidacion.push(`El item ${index + 1} no tiene código de artículo`);
          }
        });
      }

      if (erroresValidacion.length > 0) {
        erroresValidacion.forEach((error) => {
          enqueueSnackbar(error, { variant: 'error', autoHideDuration: 5000 });
        });
        setError(erroresValidacion.join('\n'));
        setLoading(false);
        return;
      }

      const errorLotes = mensajeValidacionLotes(
        (facturaVenta.detalles || []).map((d) => ({
          codigoArticulo: d.codigoArticulo,
          cantidad: d.cantidad,
          controlLoteSerie: (d as any).controlLoteSerie,
          lotes: (d as any).lotes,
          esKit: (d as any).esKit,
          lotesComponentes: (d as any).lotesComponentes,
        })),
      );
      if (errorLotes) {
        enqueueSnackbar(errorLotes, { variant: 'warning', autoHideDuration: 6000 });
        setLoading(false);
        return;
      }

      advertirLineasConPrecioCero(facturaVenta.detalles, enqueueSnackbar, 'factura');

      const errPrecioMin = primerErrorPrecioMinimoLineas(
        (facturaVenta.detalles || []).map((d) => ({
          codigoArticulo: d.codigoArticulo,
          precioUnitario: d.precioUnitario,
          descuento: d.descuento ?? d.porcDesc,
          precioMinimo: d.precioMinimo,
        })),
      );
      if (errPrecioMin) {
        enqueueSnackbar(errPrecioMin, { variant: 'error' });
        setLoading(false);
        return;
      }

      if (modoEditar && nroFormularioParam) {
        const id = parseInt(nroFormularioParam, 10);
        if (!isNaN(id)) {
          const fechaEmisionDate = facturaVenta.fechaEmision instanceof Date
            ? facturaVenta.fechaEmision
            : new Date(facturaVenta.fechaEmision);
          const fechaVencimDate = facturaVenta.fechaVencim instanceof Date
            ? facturaVenta.fechaVencim
            : facturaVenta.fechaVencim
              ? new Date(facturaVenta.fechaVencim)
              : fechaEmisionDate;
          const payload = {
            ...facturaVenta,
            nroSuc: facturaVenta.nroSuc || 0,
            ordenCompra: facturaVenta.ordenCompra || '',
            transporte: facturaVenta.transporte || '',
            ubicacion: facturaVenta.ubicacion || '',
            formaDePago: facturaVenta.formaDePago || '',
            formaEntrega: (facturaVenta as any).formaEntrega || facturaVenta.transporte || '',
            nroUbicacionOrigen: (facturaVenta as any).nroUbicacionOrigen,
            fechaEmision: fechaEmisionDate,
            fechaVencim: fechaVencimDate,
          } as any;
          await facturaVentaService.updateFacturaVenta(id, payload);
          enqueueSnackbar('Factura actualizada exitosamente', { variant: 'success', autoHideDuration: 3000 });
          setTimeout(() => navigate('/ventas/facturas'), 1500);
          setLoading(false);
          return;
        }
      }

      const payload = {
        ...facturaVenta,
        nroSuc: facturaVenta.nroSuc || 0,
        ordenCompra: facturaVenta.ordenCompra || '',
        transporte: facturaVenta.transporte || '',
        ubicacion: facturaVenta.ubicacion || '',
        formaDePago: facturaVenta.formaDePago || '',
        fechaEmision: facturaVenta.fechaEmision instanceof Date ? facturaVenta.fechaEmision : new Date(facturaVenta.fechaEmision),
        fechaVencim: facturaVenta.fechaVencim instanceof Date
          ? facturaVenta.fechaVencim
          : facturaVenta.fechaVencim
            ? new Date(facturaVenta.fechaVencim)
            : (facturaVenta.fechaEmision instanceof Date ? facturaVenta.fechaEmision : new Date(facturaVenta.fechaEmision)),
        horaEmision: facturaVenta.horaEmision instanceof Date ? facturaVenta.horaEmision : (facturaVenta.horaEmision ? new Date(facturaVenta.horaEmision) : new Date()),
        nroFormularioPedido: documentoOrigenSigueEnRenglones(pedidoId, facturaVenta.detalles || [])
          ? pedidoId ?? undefined
          : undefined,
        nroFormularioCotizacion: cotizacionId && !isNaN(cotizacionId) ? cotizacionId : undefined,
      } as any;
      
      const response = await facturaVentaService.createFacturaVenta(payload);
      
      let nroFactura: number | null = null;
      if (response?.nroFormulario) {
        nroFactura = response.nroFormulario;
      } else if ((response as any)?.formulario?.nroFormulario) {
        nroFactura = (response as any).formulario.nroFormulario;
      } else if ((response as any)?.data?.nroFormulario) {
        nroFactura = (response as any).data.nroFormulario;
      }
      
      if (nroFactura) {
        try {
          const facturaCargada = await facturaVentaService.getFacturaVenta(nroFactura);
          
          // Procesar los detalles para usar precioLista y porcDesc del GET
          const detallesProcesados = normalizarDetallesFacturaVentaParaEdicion(facturaCargada.detalles);
          
          setFacturaVenta({
            ...facturaCargada,
            detalles: detallesProcesados,
            ordenCompra: facturaVenta.ordenCompra || '',
            transporte: facturaVenta.transporte || '',
            ubicacion: facturaVenta.ubicacion || '',
            formaDePago: facturaVenta.formaDePago || '',
            nroSuc: facturaVenta.nroSuc || 0,
            cuit: facturaVenta.cuit || '',
            dni: facturaVenta.dni || '',
            tipoIVA: facturaVenta.tipoIVA || '',
            idCompania: facturaVenta.idCompania || '',
          } as FacturaVentaUIExt);

          const nroDoc = formatNroDocumentoVenta(
            facturaCargada.letra,
            (facturaCargada as { nroSuc?: number }).nroSuc,
            facturaCargada.nroComprobante,
          );
          setNroFacturaCreada(nroFactura);
          setComprobanteFacturaCreada(nroDoc || null);
          setSuccessDialogOpen(true);

          enqueueSnackbar(
            nroDoc ? `Factura ${nroDoc} creada exitosamente` : 'Factura creada exitosamente',
            { variant: 'success', autoHideDuration: 3000 },
          );
        } catch (loadError: any) {
          console.error('Error al cargar la factura:', loadError);
          enqueueSnackbar('Factura creada exitosamente, pero no se pudo cargar la información completa', { variant: 'warning' });
          setFacturaVenta({
            nroFormulario: 0,
            nroTipoFormulario: '12',
            tipoCliente: 'C',
            fechaEmision: new Date(),
            horaEmision: new Date(),
            nroCliente: 0,
            nombre: '',
            nroVendedor: 0,
            nroLista: '',
            codigoMoneda: 0,
            importeTotal: 0,
            asentado: 'N',
            descripcion: '',
            usuario: '',
            ordenCompra: '',
            transporte: '',
            ubicacion: '',
            formaDePago: '',
            nroSuc: 0,
            cuit: '',
            dni: '',
            tipoIVA: '',
            idCompania: '',
            detalles: [],
          });
        }
      } else {
        enqueueSnackbar('Factura de venta guardada exitosamente', { variant: 'success' });
        setFacturaVenta({
          nroFormulario: 0,
          nroTipoFormulario: '12',
          tipoCliente: 'C',
          fechaEmision: new Date(),
          horaEmision: new Date(),
          nroCliente: 0,
          nombre: '',
          nroVendedor: 0,
          nroLista: '',
          codigoMoneda: 0,
          importeTotal: 0,
          asentado: 'N',
          descripcion: '',
          usuario: '',
          ordenCompra: '',
          transporte: '',
          nroSuc: 0,
          cuit: '',
          dni: '',
          tipoIVA: '',
          idCompania: '',
          detalles: [],
        });
      }
    } catch (err: any) {
      console.error('Error al guardar factura:', err);
      console.error('Error completo:', {
        status: err?.response?.status,
        statusText: err?.response?.statusText,
        data: err?.response?.data,
        message: err?.message,
        response: err?.response
      });
      
      const status = err?.response?.status;
      const data = err?.response?.data;
      let errorMessage = 'Error al guardar la factura de venta';
      let mostrado = false;

      // Primero intentar capturar el mensaje directamente de la respuesta del backend
      // Esto captura errores como "No hay suficiente stock disponible..."
      if (data && typeof data === 'string') {
        errorMessage = data;
        enqueueSnackbar(errorMessage, { variant: 'error', autoHideDuration: 8000 });
        setError(errorMessage);
        mostrado = true;
      } else if (data?.message && typeof data.message === 'string') {
        errorMessage = data.message;
        // Si el mensaje contiene "stock" o "disponible", mostrarlo directamente
        if (errorMessage.toLowerCase().includes('stock') || errorMessage.toLowerCase().includes('disponible')) {
          enqueueSnackbar(errorMessage, { variant: 'error', autoHideDuration: 8000 });
          setError(errorMessage);
          mostrado = true;
        }
      }

      if (err?.message && (!err?.response || err.message.includes('Se encontraron') || err.message.includes('obligatorio') || err.message.includes('obligatoria'))) {
        errorMessage = err.message;
        
        let errores: string[] = [];
        if (err.message.includes('Se encontraron')) {
          const partes = err.message.split('\n');
          errores = partes.slice(1).map((e: string) => e.replace(/^\d+\.\s*/, '').trim()).filter((e: string) => e);
        } else if (err.message.includes('\n')) {
          errores = err.message.split('\n').map((e: string) => e.trim()).filter((e: string) => e && !e.includes('Errores de validación'));
        } else {
          errores = [err.message];
        }
        
        errores.forEach((error: string) => {
          if (error.trim()) {
            enqueueSnackbar(error.trim(), { variant: 'error', autoHideDuration: 6000 });
          }
        });
        
        if (errores.length > 1) {
          setError(`Se encontraron ${errores.length} errores:\n${errores.map((e, i) => `${i + 1}. ${e}`).join('\n')}`);
        } else if (errores.length === 1) {
          setError(errores[0]);
        } else {
          setError(errorMessage);
        }
        mostrado = true;
      }
      else if (data?.errors && typeof data.errors === 'object') {
        try {
          const msgs: string[] = [];
          for (const k of Object.keys(data.errors)) {
            const val = data.errors[k];
            if (Array.isArray(val)) {
              msgs.push(`${k}: ${val.join(' - ')}`);
            } else if (val) {
              msgs.push(`${k}: ${String(val)}`);
            }
          }
          if (msgs.length > 0) {
            errorMessage = `Error de validación del servidor:\n${msgs.join('\n')}`;
            msgs.forEach((msg: string) => {
              enqueueSnackbar(msg, { variant: 'error', autoHideDuration: 6000 });
            });
            setError(errorMessage);
            mostrado = true;
          }
        } catch {}
      }
      else if (!mostrado) {
        if (status === 400) {
          // Intentar capturar el mensaje de diferentes formas
          errorMessage = data?.title || data?.message || data?.error || (typeof data === 'string' ? data : 'Solicitud inválida. Revise los datos ingresados.');
          
          // Si data es un string directo, usarlo
          if (typeof data === 'string' && data.trim()) {
            errorMessage = data.trim();
          } else if (data?.message) {
            errorMessage = data.message;
          } else if (data?.error) {
            errorMessage = data.error;
          }
          
          if (errorMessage.includes('\n')) {
            errorMessage.split('\n').forEach((msg: string) => {
              if (msg.trim()) {
                enqueueSnackbar(msg.trim(), { variant: 'error', autoHideDuration: 6000 });
              }
            });
          } else {
            enqueueSnackbar(errorMessage, { variant: 'error', autoHideDuration: 8000 });
          }
          setError(errorMessage);
          mostrado = true;
        } else if (status === 403) {
          notifyPermissionDenied(enqueueSnackbar, err);
          errorMessage = 'No tenés permisos para crear facturas.';
          setError(errorMessage);
          mostrado = true;
        } else if (status === 401) {
          errorMessage = 'No autorizado para crear facturas. Verifique su sesión.';
          enqueueSnackbar(errorMessage, { variant: 'error' });
          setError(errorMessage);
          mostrado = true;
        } else if (status === 404) {
          const urlIntentada = err?.config?.url || 'desconocida';
          errorMessage = `Endpoint no encontrado (404). URL intentada: ${urlIntentada}. Verifique que el endpoint esté disponible en el servidor.`;
          enqueueSnackbar(errorMessage, { variant: 'error', autoHideDuration: 10000 });
          setError(errorMessage);
          mostrado = true;
        } else if (status === 500) {
          errorMessage = data?.message || 'Error interno del servidor. Intente nuevamente o contacte con soporte.';
          enqueueSnackbar(errorMessage, { variant: 'error', autoHideDuration: 10000 });
          setError(errorMessage);
          mostrado = true;
        } else if (data?.message) {
          errorMessage = data.message;
          if (errorMessage.includes('\n')) {
            errorMessage.split('\n').forEach((msg: string) => {
              if (msg.trim()) {
                enqueueSnackbar(msg.trim(), { variant: 'error', autoHideDuration: 6000 });
              }
            });
          } else {
            enqueueSnackbar(errorMessage, { variant: 'error', autoHideDuration: 8000 });
          }
          setError(errorMessage);
          mostrado = true;
        }
      }

      if (!mostrado) {
        const messageText = (data?.message || data || err?.message || '').toString();
        if (messageText && typeof messageText === 'string' && messageText.length > 0) {
          errorMessage = messageText.length > 200 ? messageText.substring(0, 200) + '...' : messageText;
          if (errorMessage.includes('\n')) {
            errorMessage.split('\n').forEach((msg: string) => {
              if (msg.trim()) {
                enqueueSnackbar(msg.trim(), { variant: 'error', autoHideDuration: 6000 });
              }
            });
          } else {
            enqueueSnackbar(errorMessage, { variant: 'error', autoHideDuration: 10000 });
          }
          setError(errorMessage);
        } else {
          errorMessage = 'Error al guardar la factura de venta. Intente nuevamente.';
          enqueueSnackbar(errorMessage, { variant: 'error' });
          setError(errorMessage);
        }
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCloseSuccessDialog = () => {
    setSuccessDialogOpen(false);
    resetOfferedTracking();
    navigate('/ventas/facturas');
  };

  // Handler para cambios en el formulario
  const handleFacturaChange = (factura: FacturaVenta) => {
    setFacturaVenta({
      ...facturaVenta,
      ...factura,
      fechaEmision: factura.fechaEmision ?? facturaVenta.fechaEmision,
      fechaVencim: factura.fechaVencim ?? facturaVenta.fechaVencim,
      vtoComprob: (factura as FacturaVentaUI).vtoComprob ?? facturaVenta.vtoComprob,
      // Preservar campos adicionales
      ordenCompra: (factura as FacturaVentaUI).ordenCompra ?? facturaVenta.ordenCompra ?? '',
      transporte: (factura as FacturaVentaUI).transporte ?? facturaVenta.transporte ?? '',
      ubicacion: (factura as FacturaVentaUI).ubicacion ?? facturaVenta.ubicacion ?? '',
      formaDePago: (factura as FacturaVentaUI).formaDePago ?? facturaVenta.formaDePago ?? '',
      nroSuc: (factura as FacturaVentaUI).nroSuc ?? facturaVenta.nroSuc ?? 0,
      cuit: (factura as FacturaVentaUI).cuit ?? facturaVenta.cuit ?? '',
      dni: (factura as FacturaVentaUI).dni ?? facturaVenta.dni ?? '',
      tipoIVA: (factura as FacturaVentaUI).tipoIVA ?? facturaVenta.tipoIVA ?? '',
      idCompania: (factura as any).idCompania ?? facturaVenta.idCompania ?? '',
    } as FacturaVentaUIExt);
  };

  const identificadorComprobante = (modoVer || modoEditar) && facturaVenta?.letra && (facturaVenta?.nroSuc ?? 0) > 0 && (facturaVenta?.nroComprobante ?? 0) > 0
    ? `${facturaVenta.letra}-${String(facturaVenta.nroSuc ?? 0).padStart(5, '0')}-${String(facturaVenta.nroComprobante ?? 0).padStart(8, '0')}`
    : '';

  const puedeGenerarAsiento =
    modoVer && superAdmin && (facturaVenta.importeTotal ?? 0) > 0 && (facturaVenta.nroFormulario ?? 0) > 0;

  const ejecutarGenerarAsiento = async () => {
    const id = facturaVenta.nroFormulario;
    setOpenGenerarAsientoDialog(false);
    setGenerandoAsiento(true);
    try {
      const r = await facturaVentaService.regenerarAsientoFacturaVenta(id);
      enqueueSnackbar(r.message ?? `Asiento generado (Nº ${r.nroAsiento}).`, { variant: 'success' });
      setFacturaVenta((prev) => ({ ...prev, nroAsiento: r.nroAsiento }));
      navigate(`/contabilidad/asientos-diario/${r.nroAsiento}`);
    } catch (e: unknown) {
      const ax = e as { response?: { data?: { message?: string } } };
      const msg = ax.response?.data?.message ?? 'No se pudo generar el asiento.';
      enqueueSnackbar(msg, { variant: 'error' });
    } finally {
      setGenerandoAsiento(false);
    }
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh', pb: { xs: 12, sm: 11, md: 10 }, maxWidth: '100%', overflowX: 'hidden' }}>
      <Card 
        sx={{ 
          bgcolor: '#ffffff',
          borderRadius: 3,
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          overflow: 'hidden'
        }}
      >
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} spacing={1.5} sx={{ mb: 3, flexWrap: 'wrap', gap: 1 }}>
            <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ xs: 'flex-start', sm: 'center' }} spacing={1.5} sx={{ flexWrap: 'wrap', gap: 1 }}>
              <Button
                startIcon={<ArrowBackIcon />}
                onClick={() => {
                  if (modoVer) {
                    navigate('/ventas/facturas');
                    return;
                  }
                  setConfirmSalirOpen(true);
                }}
                sx={{ textTransform: 'none' }}
              >
                Facturas de Venta
              </Button>
              <Typography
                variant="h4"
                sx={{ fontWeight: 600, color: '#1976d2' }}
              >
                {modoVer
                  ? `Ver Factura de Venta${identificadorComprobante ? ` ${identificadorComprobante}` : ''}`
                  : modoEditar
                    ? `Editar Factura de Venta${identificadorComprobante ? ` ${identificadorComprobante}` : ''}`
                    : 'Nueva Factura de Venta'}
              </Typography>
            </Stack>
            {modoVer || modoEditar ? (
              <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
                {modoVer && superAdmin && (
                  <Tooltip
                    title={
                      puedeGenerarAsiento
                        ? 'Elimina el/los asiento(s) vinculados y genera uno nuevo con la lógica actual (solo SUPERADMIN).'
                        : 'Se requiere importe total positivo.'
                    }
                  >
                    <span>
                      <Button
                        variant="outlined"
                        color="warning"
                        startIcon={<SyncIcon />}
                        disabled={!puedeGenerarAsiento || generandoAsiento}
                        onClick={() => setOpenGenerarAsientoDialog(true)}
                        sx={{
                          borderRadius: 2,
                          px: 3,
                          py: 1.5,
                          textTransform: 'none',
                          fontWeight: 500,
                          boxShadow: '0 2px 4px rgba(0,0,0,0.08)',
                        }}
                      >
                        Generar asiento
                      </Button>
                    </span>
                  </Tooltip>
                )}
                <VerAsientoContableButton
                  nroAsiento={facturaVenta.nroAsiento}
                  variant="outlined"
                  color="secondary"
                  startIcon={<AccountBalanceOutlinedIcon />}
                  sx={{
                    borderRadius: 2,
                    px: 3,
                    py: 1.5,
                    textTransform: 'none',
                    fontWeight: 500,
                    boxShadow: '0 2px 4px rgba(0,0,0,0.08)',
                  }}
                />
              </Stack>
            ) : (pedidoId || cotizacionId) && infoPedido ? (
              <Chip
                label={
                  infoPedido.letra && (infoPedido.puntoVenta ?? 0) > 0 && (infoPedido.nroComprobante ?? 0) > 0
                    ? `Facturando ${cotizacionId ? 'cotización' : 'pedido'} ${infoPedido.letra}-${String(infoPedido.puntoVenta ?? 0).padStart(5, '0')}-${String(infoPedido.nroComprobante ?? 0).padStart(8, '0')}`
                    : `Facturando ${cotizacionId ? 'cotización' : 'pedido'} #${infoPedido.nroFormulario || pedidoId || cotizacionId}`
                }
                color="info"
                sx={{ fontWeight: 500 }}
              />
            ) : null}
          </Stack>

          {error && (
            <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
              {error}
            </Alert>
          )}

          {loadingPedido && (
            <Alert severity="info" sx={{ mb: 3, borderRadius: 2 }}>
              Cargando datos del pedido...
            </Alert>
          )}

          <FacturaForm
            facturaVenta={facturaVenta}
            onFacturaChange={handleFacturaChange}
            onClienteSearch={() => setClienteDialogOpen(true)}
            onProductoSearch={() => setProductoDialogOpen(true)}
            onSave={handleSave}
            onCancel={() =>
              window.history.length > 1 ? navigate(-1) : navigate('/ventas/facturas')
            }
            loading={loading}
            saveButtonText={modoEditar ? 'Actualizar Factura' : 'Guardar Factura'}
            readOnly={modoVer}
          />
        </CardContent>
      </Card>

      <ConfirmCancelHotkeyDialog
        open={confirmSalirOpen}
        onDismiss={() => setConfirmSalirOpen(false)}
        onConfirm={() => {
          setConfirmSalirOpen(false);
          if (window.history.length > 1) {
            navigate(-1);
          } else {
            navigate('/ventas/facturas');
          }
        }}
      />
      <ClienteSearch
        open={clienteDialogOpen}
        onClose={() => setClienteDialogOpen(false)}
        onSelect={handleClienteSelect}
      />

      <ProductoSearch
        open={productoDialogOpen}
        onClose={() => setProductoDialogOpen(false)}
        onSearch={handleProductoSearch}
        onSelect={handleProductoSelect}
        productos={productos}
        searchTerm={searchTerm}
        onSearchTermChange={setSearchTerm}
        loading={loading}
      />

      <Dialog
        open={successDialogOpen}
        onClose={handleCloseSuccessDialog}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: 6,
          }
        }}
      >
        <DialogTitle
          sx={{
            fontWeight: 700,
            fontSize: '1.25rem',
            pb: 1.5,
            pt: 3,
            px: 3,
          }}
        >
          Factura Creada
        </DialogTitle>
        <DialogContent sx={{ pt: 1, pb: 2, px: 3 }}>
          <Typography variant="body1" sx={{ color: 'text.secondary', lineHeight: 1.6 }}>
            Factura creada exitosamente!{comprobanteFacturaCreada ? ` Comprobante: ${comprobanteFacturaCreada}` : ''}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, pt: 1, justifyContent: 'flex-end' }}>
          <Button
            onClick={handleCloseSuccessDialog}
            variant="text"
            color="primary"
            sx={{
              minWidth: 80,
              textTransform: 'none',
              fontWeight: 500,
              color: 'primary.main',
            }}
          >
            OK
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openGenerarAsientoDialog} onClose={() => setOpenGenerarAsientoDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Generar asiento contable</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ pt: 0.5 }}>
            Se eliminarán todas las líneas del libro diario vinculadas a esta factura (incluido si el asiento estaba
            contabilizado) y se creará un asiento nuevo con los importes y cuentas según la configuración actual.
            ¿Continuar?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenGenerarAsientoDialog(false)} disabled={generandoAsiento}>
            Cancelar
          </Button>
          <Button variant="contained" color="warning" onClick={ejecutarGenerarAsiento} disabled={generandoAsiento}>
            {generandoAsiento ? 'Procesando…' : 'Confirmar'}
          </Button>
        </DialogActions>
      </Dialog>

      {postEmisionEmailUi}
    </Box>
  );
};

export default NuevaFacturaVenta;

